import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

export const MOSAIC_CARD_LEGACY_FIELD_ORDER = [
  "heading",
  "link_url",
  "link_target",
  "distribution_mode",
  "background_image",
  "width",
  "aspect-ratio",
  "background_style_contain",
  "font",
  "font_size",
  "text_color",
  "shadow",
  "rounded",
  "background_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  mosaicCardBlockDescriptor: _generatedMosaicCardBlockDescriptor,
  mosaicCardBlockBlocDefinition,
  mosaicCardBlockDashboardLayout,
  mosaicCardBlockConfig,
  mosaicCardBlockResolveConfig,
  mosaicCardBlockProperties,
} = doodBlockConfig(
  {
    name: "Mosaic card",
    componentType: "MosaicCardBlockComponent",
    category: 'layout',
    description: "Clickable card rendered inside a Mosaic block.",
    keywords: ["mosaic", "card", "marketing"],
    allowedIn: [{ bloc: "MosaicBlockComponent", slot: "cards" }],
    sharedFields: {},
  },

  () => ({
    heading: propertyValue({
      type: "text",
      value: "",
      options: { label: "pages.blocks.mosaic-card.heading.label" },
    }),
    link_url: propertyValue({
      type: "link",
      value: "",
      options: {
        label: "pages.blocks.mosaic-card.link-url.label",
        help: "pages.blocks.mosaic-card.link-url.help",
        defaultMode: "page",
        modes: ["page", "modal", "mailto", "external", "navigationBack", "anchor"],
      },
    }),
    link_target: propertyValue({
      type: "select",
      value: "_self",
      options: {
        label: "pages.blocks.mosaic-card.link-target.label",
        help: "pages.blocks.mosaic-card.link-target.help",
        choices: [
          {
            value: "_self",
            label: "pages.blocks.mosaic-card.link-target.options._self",
          },
          {
            value: "_blank",
            label: "pages.blocks.mosaic-card.link-target.options._blank",
          },
        ],
      },
    }),
    distribution_mode: propertyValue({
      type: "select",
      value: "",
      options: {
        label: "pages.blocks.mosaic-card.distribution-mode.label",
        help: "pages.blocks.mosaic-card.distribution-mode.help",
        choices: [
          {
            value: "",
            label: "pages.blocks.mosaic-card.distribution-mode.options.none",
          },
          {
            value: "click-and-collect",
            label: "pages.blocks.mosaic-card.distribution-mode.options.click-and-collect",
          },
          {
            value: "delivery",
            label: "pages.blocks.mosaic-card.distribution-mode.options.delivery",
          },
          {
            value: "eat-in",
            label: "pages.blocks.mosaic-card.distribution-mode.options.eat-in",
          },
          {
            value: "on-site",
            label: "pages.blocks.mosaic-card.distribution-mode.options.on-site",
          },
          {
            value: "shipping",
            label: "pages.blocks.mosaic-card.distribution-mode.options.shipping",
          },
        ],
      },
    }),
    prompt_cart_parameters: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label: "pages.blocks.mosaic-card.prompt-cart-parameters.label",
        help: "pages.blocks.mosaic-card.prompt-cart-parameters.help",
      },
    }),
    background_image: propertyValue({
      type: "image",
      value: null,
      options: {
        label: "pages.blocks.mosaic-card.background-image.label",
        help: "pages.blocks.mosaic-card.background-image.help",
      },
    }),
    // `width` drives the column span in the Mosaic 2-column grid AND the
    // default aspect ratio (100/64 for one column, 100/32 for two columns),
    // so both spans render with the same height unless `aspect-ratio` is set.
    width: propertyValue({
      type: "select",
      value: "one",
      options: {
        label: "pages.blocks.mosaic-card.width.label",
        choices: [
          {
            value: "one",
            label: "pages.blocks.mosaic-card.width.options.one",
          },
          {
            value: "two",
            label: "pages.blocks.mosaic-card.width.options.two",
          },
        ],
      },
    }),
    "aspect-ratio": propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.mosaic-card.aspect-ratio.label",
        help: "pages.blocks.mosaic-card.aspect-ratio.help",
      },
    }),
    background_style_contain: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label: "pages.blocks.mosaic-card.background-style-contain.label",
        help: "pages.blocks.mosaic-card.background-style-contain.help",
      },
    }),
    font: propertyValue({
      type: "select",
      value: "accent",
      options: {
        label: "pages.blocks.mosaic-card.font.label",
        choices: [
          {
            value: "accent",
            label: "pages.blocks.mosaic-card.font.options.accent",
          },
          {
            value: "brand",
            label: "pages.blocks.mosaic-card.font.options.brand",
          },
        ],
      },
    }),
    font_size: propertyValue({
      type: "select",
      value: "sm",
      options: {
        label: "pages.blocks.mosaic-card.font-size.label",
        choices: [
          {
            value: "sm",
            label: "pages.blocks.mosaic-card.font-size.options.sm",
          },
          {
            value: "md",
            label: "pages.blocks.mosaic-card.font-size.options.md",
          },
          {
            value: "lg",
            label: "pages.blocks.mosaic-card.font-size.options.lg",
          },
          {
            value: "xl",
            label: "pages.blocks.mosaic-card.font-size.options.xl",
          },
          {
            value: "2xl",
            label: "pages.blocks.mosaic-card.font-size.options.2xl",
          },
          {
            value: "3xl",
            label: "pages.blocks.mosaic-card.font-size.options.3xl",
          },
          {
            value: "4xl",
            label: "pages.blocks.mosaic-card.font-size.options.4xl",
          },
          {
            value: "5xl",
            label: "pages.blocks.mosaic-card.font-size.options.5xl",
          },
          {
            value: "6xl",
            label: "pages.blocks.mosaic-card.font-size.options.6xl",
          },
          {
            value: "7xl",
            label: "pages.blocks.mosaic-card.font-size.options.7xl",
          },
          {
            value: "8xl",
            label: "pages.blocks.mosaic-card.font-size.options.8xl",
          },
        ],
      },
    }),
    text_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#000000" },
      options: { label: "pages.blocks.mosaic-card.text-color.label" },
    }),
    shadow: propertyValue({
      type: "select",
      value: "small",
      options: {
        label: "pages.blocks.mosaic-card.shadow.label",
        choices: [
          {
            value: "none",
            label: "pages.blocks.mosaic-card.shadow.options.none",
          },
          {
            value: "small",
            label: "pages.blocks.mosaic-card.shadow.options.small",
          },
          {
            value: "medium",
            label: "pages.blocks.mosaic-card.shadow.options.medium",
          },
          {
            value: "large",
            label: "pages.blocks.mosaic-card.shadow.options.large",
          },
        ],
      },
    }),
    rounded: propertyValue({
      type: "select",
      value: "large",
      options: {
        label: "pages.blocks.mosaic-card.rounded.label",
        choices: [
          {
            value: "none",
            label: "pages.blocks.mosaic-card.rounded.options.none",
          },
          {
            value: "small",
            label: "pages.blocks.mosaic-card.rounded.options.small",
          },
          {
            value: "medium",
            label: "pages.blocks.mosaic-card.rounded.options.medium",
          },
          {
            value: "large",
            label: "pages.blocks.mosaic-card.rounded.options.large",
          },
          {
            value: "2xl",
            label: "pages.blocks.mosaic-card.rounded.options.2xl",
          },
          {
            value: "3xl",
            label: "pages.blocks.mosaic-card.rounded.options.3xl",
          },
          {
            value: "4xl",
            label: "pages.blocks.mosaic-card.rounded.options.4xl",
          },
          {
            value: "full",
            label: "pages.blocks.mosaic-card.rounded.options.full",
          },
        ],
      },
    }),
    background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#FFFFFF" },
      options: { label: "pages.blocks.mosaic-card.background-color.label" },
    }),
  }),

  () => ({
    omitFields: ["prompt_cart_parameters"] as const,
    tabs: [
      {
        label: "pages.blocks.mosaic-card.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.mosaic-card.fieldset.media.label",
            fields: [
              "background_image",
              "background_style_contain",
              "background_color",
            ],
          },
          {
            legend: "pages.blocks.mosaic-card.fieldset.title.label",
            fields: ["heading", "font", "font_size", "text_color"],
          },
          {
            legend: "pages.blocks.mosaic-card.fieldset.link.label",
            fields: ["link_url", "link_target", "distribution_mode"],
          },
          {
            legend: "pages.blocks.mosaic-card.fieldset.layout.label",
            fields: [
              // Same prop, contextual wording: without a custom ratio the
              // default ratio compensates the span (same rendered height), so
              // the field reads as a card "format"; with a custom ratio it is
              // a pure column-width choice.
              {
                prop: "width",
                label: "pages.blocks.mosaic-card.width.label-auto-ratio",
                help: "pages.blocks.mosaic-card.width.help-auto-ratio",
                visibleWhen: { "aspect-ratio": { nonEmpty: false } },
              },
              {
                prop: "width",
                label: "pages.blocks.mosaic-card.width.label",
                help: "pages.blocks.mosaic-card.width.help-custom-ratio",
                visibleWhen: { "aspect-ratio": { nonEmpty: true } },
              },
              "aspect-ratio",
              "shadow",
              "rounded",
            ],
          },
        ],
      },
    ],
  }),
);

function withoutOmittedCb1Fields(descriptor: IBlockDescriptor): IBlockDescriptor {
  const omitted = new Set(mosaicCardBlockDashboardLayout.omitFields ?? []);
  if (omitted.size === 0) {
    return descriptor;
  }
  return {
    ...descriptor,
    fields: (descriptor.fields ?? []).filter(
      field => !("handle" in field) || !omitted.has(field.handle),
    ),
  };
}

export function mosaicCardBlockDescriptor(): IBlockDescriptor;
export function mosaicCardBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function mosaicCardBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedMosaicCardBlockDescriptor;
  }
  return withoutOmittedCb1Fields(
    buildOrderedBlockDescriptor(
      _generatedMosaicCardBlockDescriptor,
      mosaicCardBlockProperties,
      fieldOrder,
    ),
  );
}

export {
  mosaicCardBlockBlocDefinition,
  mosaicCardBlockDashboardLayout,
  mosaicCardBlockConfig,
  mosaicCardBlockResolveConfig,
  mosaicCardBlockProperties,
};

export type MosaicCardBlockConfig = typeof mosaicCardBlockConfig;
